import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { 
  ArrowLeft, Sparkles, Globe, Mic, Video, Heart, Share2, 
  Star, Calendar, MessageSquare, Play, Image as ImageIcon,
  CheckCircle, User, Briefcase, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const tierColors = {
  standard: 'from-cyan-400 to-blue-500',
  premium: 'from-purple-400 to-pink-500',
  luxury: 'from-amber-400 to-orange-500',
  custom: 'from-emerald-400 to-teal-500'
};

const categoryLabels = {
  brand_ambassador: 'Brand Ambassador',
  host: 'Event Host',
  influencer: 'Digital Influencer',
  presenter: 'Corporate Presenter',
  mc: 'Master of Ceremonies'
};

const useCaseLabels = {
  events: 'Live Events',
  digital_campaigns: 'Digital Campaigns',
  brand_activations: 'Brand Activations',
  trade_shows: 'Trade Shows',
  product_launches: 'Product Launches',
  conferences: 'Conferences',
  corporate_events: 'Corporate Events',
  webinars: 'Webinars',
  training_sessions: 'Training',
  influencer_campaigns: 'Influencer Campaigns',
  social_events: 'Social Events',
  award_ceremonies: 'Award Ceremonies',
  corporate_galas: 'Corporate Galas',
  charity_events: 'Charity Events',
  luxury_launches: 'Luxury Launches',
  vip_experiences: 'VIP Experiences',
  exclusive_events: 'Exclusive Events',
  high_fashion: 'High Fashion',
  private_showcases: 'Private Showcases',
  luxury_events: 'Luxury Events',
  fashion_shows: 'Fashion Shows',
  product_demos: 'Product Demos',
  formal_dinners: 'Formal Dinners'
};

const capabilityInfo = {
  multilingual: { icon: Globe, label: 'Multilingual', desc: 'Speaks multiple languages fluently' },
  scripted_delivery: { icon: Mic, label: 'Scripted Delivery', desc: 'Perfect for prepared presentations' },
  virtual_presence: { icon: Video, label: 'Virtual Presence', desc: 'Engaging digital presence' },
  live_interaction: { icon: MessageSquare, label: 'Live Interaction', desc: 'Real-time audience engagement' },
  brand_storytelling: { icon: Briefcase, label: 'Brand Storytelling', desc: 'Compelling narrative delivery' },
  data_presentation: { icon: CheckCircle, label: 'Data Presentation', desc: 'Clear data communication' },
  q_and_a_handling: { icon: MessageSquare, label: 'Q&A Handling', desc: 'Professional Q&A sessions' },
  audience_engagement: { icon: User, label: 'Audience Engagement', desc: 'Active audience participation' },
  social_media_integration: { icon: Share2, label: 'Social Media', desc: 'Integrated social presence' },
  event_management: { icon: Calendar, label: 'Event Management', desc: 'Seamless event flow' },
  vip_engagement: { icon: Star, label: 'VIP Engagement', desc: 'High-touch VIP interactions' }
};

const AmbassadorProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ambassador, setAmbassador] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const galleryImages = (ambassador?.media || []).filter((m) => m.type !== 'video');
  const lightboxOpen = lightboxIdx !== null && lightboxIdx >= 0;
  const showNav = galleryImages.length > 1;
  const goPrev = () => setLightboxIdx((i) => (i - 1 + galleryImages.length) % galleryImages.length);
  const goNext = () => setLightboxIdx((i) => (i + 1) % galleryImages.length);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft' && showNav) goPrev();
      else if (e.key === 'ArrowRight' && showNav) goNext();
      else if (e.key === 'Escape') setLightboxIdx(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAmbassador();
  }, [slug]);

  useEffect(() => {
    if (ambassador && user) {
      checkFavoriteStatus();
    }
  }, [ambassador, user]);

  const fetchAmbassador = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/ambassadors/${slug}`);
      setAmbassador(response.data);
    } catch (error) {
      console.error('Failed to load ambassador:', error);
      toast.error('Ambassador not found');
      navigate('/discover');
    } finally {
      setLoading(false);
    }
  };

  const checkFavoriteStatus = async () => {
    if (!user) return;
    try {
      const response = await api.get(`/ambassadors/favorites/check/${ambassador.id}`);
      setIsFavorited(response.data.is_favorited);
    } catch (error) {
      console.error('Failed to check favorite status:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Please log in to add favorites');
      navigate('/login');
      return;
    }
    
    try {
      if (isFavorited) {
        await api.delete(`/ambassadors/favorites/${ambassador.id}`);
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await api.post('/ambassadors/favorites', { ambassador_id: ambassador.id });
        setIsFavorited(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${ambassador.name} - AI Ambassador`,
          text: ambassador.tagline,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleBooking = () => {
    if (!user) {
      toast.error('Please log in to book');
      navigate('/login');
      return;
    }
    // Navigate to ambassador booking wizard
    navigate(`/client/book-ambassador/${ambassador.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!ambassador) {
    return null;
  }

  const gradientClass = tierColors[ambassador.tier] || tierColors.standard;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ambassador-profile-page">
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${gradientClass} text-white`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="py-4">
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate(-1)}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          
          {/* Profile Header. Iter-297d — added z-index + face-safe
              cropping so the circular avatar doesn't slice eyes off
              and stays on top of the gradient hero, which was the
              recurring P1 complaint. */}
          <div className="pb-8 flex flex-col md:flex-row items-center md:items-start gap-6 relative z-10">
            <div className="relative">
              <img
                src={ambassador.avatar_url}
                alt={ambassador.name}
                onClick={() => {
                  const idx = galleryImages.findIndex((g) => g.url === ambassador.avatar_url);
                  setLightboxIdx(idx >= 0 ? idx : (galleryImages.length ? 0 : null));
                }}
                className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-xl bg-slate-100 relative z-10 cursor-zoom-in hover:opacity-90 transition-opacity"
                style={{ objectPosition: '50% 25%' }}
                data-testid="ambassador-avatar-image"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(ambassador.name)}&size=160&background=6366f1&color=fff&bold=true`;
                }}
              />
              <Badge className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white text-slate-800 flex items-center gap-1 shadow-lg">
                <Sparkles className="w-3 h-3 text-purple-500" />
                AI Ambassador
              </Badge>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                <h1 className="text-3xl md:text-4xl font-bold" data-testid="ambassador-name">
                  {ambassador.name}
                </h1>
                {ambassador.tier !== 'standard' && (
                  <Badge className="bg-white/20 text-white capitalize w-fit mx-auto md:mx-0">
                    {ambassador.tier}
                  </Badge>
                )}
              </div>
              <p className="text-lg text-white/90 mb-2">
                {categoryLabels[ambassador.category]}
              </p>
              <p className="text-xl font-medium text-white/95 mb-4" data-testid="ambassador-tagline">
                {ambassador.tagline}
              </p>
              
              {/* Languages */}
              <div className="flex items-center justify-center md:justify-start gap-2 flex-wrap mb-4">
                <Globe className="w-4 h-4" />
                {ambassador.supported_languages?.map((lang) => (
                  <Badge key={lang} className="bg-white/20 text-white">
                    {lang}
                  </Badge>
                ))}
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap justify-center md:justify-start gap-3">
                <Button 
                  size="lg"
                  className="bg-white text-slate-900 hover:bg-white/90"
                  onClick={handleBooking}
                  data-testid="book-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book This Ambassador
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white/20"
                  onClick={toggleFavorite}
                  data-testid="favorite-btn"
                >
                  <Heart className={`w-4 h-4 mr-2 ${isFavorited ? 'fill-white' : ''}`} />
                  {isFavorited ? 'Saved' : 'Save'}
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white/20"
                  onClick={handleShare}
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
            
            {/* Price Card */}
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white w-full md:w-auto">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-white/80 mb-1">Activation Price</p>
                <p className="text-2xl font-bold" data-testid="ambassador-price">
                  {ambassador.price_display}
                </p>
                <p className="text-xs text-white/60 mt-1">per event</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white w-full justify-start">
            <TabsTrigger value="about" data-testid="about-tab">About</TabsTrigger>
            <TabsTrigger value="capabilities" data-testid="capabilities-tab">Capabilities</TabsTrigger>
            <TabsTrigger value="use-cases" data-testid="use-cases-tab">Use Cases</TabsTrigger>
            {ambassador.media?.length > 0 && (
              <TabsTrigger value="media" data-testid="media-tab">Media</TabsTrigger>
            )}
          </TabsList>

          {/* About Tab */}
          <TabsContent value="about" className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>About {ambassador.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed" data-testid="ambassador-description">
                  {ambassador.description}
                </p>
                
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-2">Personality</h4>
                  <p className="text-gray-600 italic" data-testid="ambassador-personality">
                    "{ambassador.personality}"
                  </p>
                </div>
                
                <div className="pt-4 border-t grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-1">Style</h4>
                    <Badge variant="outline" className="capitalize">
                      {ambassador.style}
                    </Badge>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Category</h4>
                    <Badge variant="outline">
                      {categoryLabels[ambassador.category]}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Capabilities Tab */}
          <TabsContent value="capabilities" className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Capabilities & Skills</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="capabilities-list">
                  {ambassador.capabilities?.map((cap) => {
                    const info = capabilityInfo[cap] || { icon: Sparkles, label: cap.replace(/_/g, ' '), desc: '' };
                    const Icon = info.icon;
                    return (
                      <div key={cap} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                        <div className={`w-10 h-10 bg-gradient-to-br ${gradientClass} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-medium capitalize">{info.label}</h4>
                          <p className="text-sm text-muted-foreground">{info.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Use Cases Tab */}
          <TabsContent value="use-cases" className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Ideal Use Cases</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3" data-testid="use-cases-list">
                  {ambassador.use_cases?.map((useCase) => (
                    <Badge 
                      key={useCase} 
                      className={`bg-gradient-to-r ${gradientClass} text-white px-4 py-2 text-sm`}
                    >
                      {useCaseLabels[useCase] || useCase.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Perfect For</h4>
                  <p className="text-sm text-muted-foreground">
                    {ambassador.name} excels in {ambassador.style} settings, making them ideal for 
                    {ambassador.tier === 'luxury' ? ' high-end, exclusive' : ambassador.tier === 'premium' ? ' premium, professional' : ' versatile'} events 
                    requiring {ambassador.capabilities?.slice(0, 2).join(' and ').replace(/_/g, ' ')}.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Media Tab */}
          {ambassador.media?.length > 0 && (
            <TabsContent value="media" className="space-y-6">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Media Gallery</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {ambassador.media.map((item) => (
                      <div key={item.id}
                           onClick={() => item.type !== 'video' && setLightboxIdx(galleryImages.findIndex((g) => g.id === item.id))}
                           className={`relative aspect-video bg-slate-100 rounded-lg overflow-hidden ${item.type !== 'video' ? 'cursor-zoom-in hover:opacity-90 transition-opacity' : ''}`}
                           data-testid="ambassador-gallery-item">
                        {item.type === 'video' ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Play className="w-12 h-12 text-white bg-black/50 rounded-full p-3" />
                          </div>
                        ) : (
                          <img
                            src={item.url}
                            alt={item.title || ambassador.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* CTA Section */}
        <Card className={`mt-8 bg-gradient-to-r ${gradientClass} text-white`}>
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-2">Ready to elevate your event?</h3>
            <p className="text-white/90 mb-6">
              Book {ambassador.name} for your next activation and experience the future of brand engagement.
            </p>
            <Button 
              size="lg"
              className="bg-white text-slate-900 hover:bg-white/90"
              onClick={handleBooking}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book {ambassador.name}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={lightboxOpen} onOpenChange={(o) => !o && setLightboxIdx(null)}>
        <DialogContent className="max-w-3xl bg-black/95 border-0 p-2" data-testid="ambassador-lightbox">
          {lightboxOpen && galleryImages[lightboxIdx] && (
            <div className="relative">
              <img src={galleryImages[lightboxIdx].url} alt={ambassador?.name}
                   className="w-full max-h-[80vh] object-contain rounded-lg" />
              {showNav && (
                <>
                  <button onClick={goPrev} data-testid="lightbox-prev"
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors">
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button onClick={goNext} data-testid="lightbox-next"
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors">
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full" data-testid="lightbox-counter">
                    {lightboxIdx + 1} / {galleryImages.length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AmbassadorProfile;
